import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@lib/api/client';
import { captureScreenMap } from '../lib/captureScreenMap';
import { Markdown } from './Markdown';

export interface GuidedStep {
  action: string; // navigate | highlight | fill | click | info
  target?: string; // nombre semántico (data-guide) o ruta para navigate
  value?: string;
  label?: string;
}

// Navegación por nombre → ruta (espejo del menú de AppLayout). El agente puede
// mandar el nombre de la pantalla y acá lo resolvemos a la ruta real.
const NAV_ROUTES: Record<string, string> = {
  dashboard: '/dashboard',
  facturas: '/home/main',
  'facturas activas': '/home/main',
  'archivo historico': '/home/minor',
  'nueva factura': '/invoices/new',
  clientes: '/customers',
  productos: '/products',
  reportes: '/report/reports',
  'mi empresa': '/company/edit',
  configuracion: '/company/settings',
  sucursales: '/branchoffice/manager',
  usuarios: '/user/manager',
  'gestion de usuarios': '/user/manager',
  planes: '/subscriptions/plans',
  'planes y suscripcion': '/subscriptions/plans',
  manual: '/manual',
  faq: '/faq',
};

const normalize = (s?: string) =>
  (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

function resolveRoute(target?: string): string | null {
  if (!target) return null;
  if (target.startsWith('/')) return target;
  return NAV_ROUTES[normalize(target)] ?? null;
}

const isVisible = (e: HTMLElement) => e.offsetParent !== null;

// Etiqueta "visible" de un elemento para matchear contra el target del agente.
// Para campos (input/select/textarea) NO usamos textContent (trae todas las
// opciones); usamos aria-label / placeholder / <label for> / name. Para
// botones/links: el texto, o aria-label/title/data-guide si es solo-ícono.
function labelOf(el: HTMLElement): string {
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'select' || tag === 'textarea') {
    const id = el.getAttribute('id');
    const byLabel = id ? document.querySelector(`label[for="${id}"]`)?.textContent || '' : '';
    return normalize(
      el.getAttribute('aria-label') || el.getAttribute('placeholder') || byLabel || el.getAttribute('name') || ''
    );
  }
  const text = normalize(el.textContent || '');
  if (text) return text;
  return normalize(el.getAttribute('aria-label') || el.getAttribute('title') || el.getAttribute('data-guide') || '');
}

// Busca el target dentro de un root con matching TOLERANTE: data-guide exacto →
// mejor candidato (inputs/selects/botones/links) por igualdad, substring en AMBOS
// sentidos (el agente suele describir de más, ej. target="Seleccione la dirección
// del receptor" vs campo "Seleccione la dirección") y, como último recurso,
// solapamiento de palabras. Prioriza elementos visibles y el match más específico.
function matchIn(root: ParentNode, n: string): HTMLElement | null {
  const byGuide = Array.from(root.querySelectorAll<HTMLElement>('[data-guide]')).filter(
    (e) => normalize(e.getAttribute('data-guide') || '') === n
  );
  const g = byGuide.find(isVisible) || byGuide[0];
  if (g) return g;

  const candidates = Array.from(
    root.querySelectorAll<HTMLElement>(
      'a, button, [role="button"], [role="link"], [role="combobox"], [role="menuitem"], input, select, textarea'
    )
  );
  const nTokens = new Set(n.split(/\s+/).filter((w) => w.length > 2));

  let best: { el: HTMLElement; score: number } | null = null;
  for (const el of candidates) {
    const t = labelOf(el);
    if (!t || t.length > 90) continue;
    let score = 0;
    if (t === n) score = 100;
    else if (t.includes(n)) score = 80 - (t.length - n.length) * 0.1; // el elemento contiene el target
    else if (t.length >= 6 && n.includes(t)) score = 70 - (n.length - t.length) * 0.1; // el target contiene la etiqueta
    else {
      const tTokens = t.split(/\s+/).filter((w) => w.length > 2);
      const hits = tTokens.filter((w) => nTokens.has(w)).length;
      if (hits >= 2 && hits >= tTokens.length * 0.6) score = 40 + hits;
    }
    if (score <= 0) continue;
    if (isVisible(el)) score += 10; // preferimos lo que el cliente ve ahora
    if (!best || score > best.score) best = { el, score };
  }
  return best?.el || null;
}

function findElement(target?: string, anchor?: string): HTMLElement | null {
  if (!target) return null;

  // 0) Ref del mapa de pantalla (lo más preciso: el agente eligió este elemento
  // exacto de lo que el cliente ve ahora). Ej. target="g7".
  const byRef = document.querySelector<HTMLElement>(`[data-guide-ref="${CSS.escape(target)}"]`);
  if (byRef) return byRef;

  const n = normalize(target);

  // Si hay ancla (ej. N° de factura o estado "Error"), buscamos el target DENTRO
  // de la fila/contenedor que contiene ese texto → resalta la fila correcta.
  if (anchor && anchor.trim()) {
    const an = normalize(anchor);
    const hits = Array.from(
      document.querySelectorAll<HTMLElement>('td, th, span, div, p, li, a')
    ).filter((e) => {
      const t = normalize(e.textContent || '');
      return t.includes(an) && t.length <= 140;
    });
    for (const hit of hits) {
      const row = hit.closest<HTMLElement>('tr, [role="row"], li') || hit.parentElement;
      const m = row ? matchIn(row, n) : null;
      if (m) return m;
    }
  }

  return matchIn(document, n);
}

interface Props {
  steps: GuidedStep[];
  onClose: () => void;
  // Si vienen, la guía es ITERATIVA: al terminar los pasos actuales, re-captura la
  // pantalla y le pide al agente el próximo tramo (copiloto paso a paso).
  sessionId?: string;
  goal?: string;
  // Al COMPLETAR la guía (no al cancelar), recibe el recorrido semántico ejecutado
  // → se guarda como playbook reutilizable.
  onComplete?: (journey: GuidedStep[]) => void;
}

const MAX_TURNS = 15; // wizards largos + corrección de errores de validación
const isRef = (t?: string) => !!t && /^g\d+$/.test(t);

/** Recorre los pasos guiados resaltando elementos del portal y navegando entre pantallas. */
export function GuidedTour({ steps, onClose, sessionId, goal, onComplete }: Props) {
  const navigate = useNavigate();
  const [allSteps, setAllSteps] = useState<GuidedStep[]>(steps);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadingNext, setLoadingNext] = useState(false);
  const [guideError, setGuideError] = useState<string | null>(null);
  const turns = useRef(0);
  // Recorrido SEMÁNTICO ejecutado (para guardarlo como guía reutilizable).
  const journey = useRef<GuidedStep[]>([]);
  const recorded = useRef<Set<number>>(new Set());
  // Guard de re-entrada: evita avances/fetches múltiples (ej. selects en cascada que
  // disparan varios 'change'). Se libera cuando renderiza el próximo paso.
  const advancing = useRef(false);

  const step = allSteps[index];
  const isLast = index >= allSteps.length - 1;
  const canContinue = !!sessionId && turns.current < MAX_TURNS;

  // EVITA que un modal del portal (Headless UI Dialog) se cierre al tocar el tour.
  // Headless detecta "click afuera" en fase de CAPTURA sobre document, y su único
  // escape es `event.defaultPrevented`. Registramos un listener en CAPTURA sobre
  // window (corre ANTES que el de document) que hace preventDefault() en clicks
  // dentro de nuestra UI -> Headless ve defaultPrevented y NO cierra. Nuestro
  // onClick igual se dispara (preventDefault no lo cancela). Solo vive durante el tour.
  useEffect(() => {
    const guard = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.('[data-support-ui]')) e.preventDefault();
    };
    window.addEventListener('click', guard, true);
    window.addEventListener('touchend', guard, true);
    return () => {
      window.removeEventListener('click', guard, true);
      window.removeEventListener('touchend', guard, true);
    };
  }, []);

  // Graba un paso del recorrido una sola vez (genera target semántico desde el DOM).
  const record = (s: GuidedStep, idx: number, el?: HTMLElement | null) => {
    if (recorded.current.has(idx)) return;
    recorded.current.add(idx);
    const target = isRef(s.target) ? (el?.textContent?.replace(/\s+/g, ' ').trim().slice(0, 60) || s.target) : s.target;
    journey.current.push({ action: s.action, target, value: s.value, label: s.label });
  };

  const finish = useCallback(() => {
    if (onComplete && journey.current.length) onComplete(journey.current);
    onClose();
  }, [onComplete, onClose]);

  const advance = useCallback(async () => {
    if (advancing.current) return; // ya hay un avance en curso (evita dobles fetch)
    advancing.current = true;
    if (!isLast) {
      setIndex((i) => i + 1); // `advancing` se libera al renderizar el nuevo paso
      return;
    }
    // Último paso del tramo: si es iterativa, pedimos el siguiente tramo.
    if (canContinue) {
      setLoadingNext(true);
      setGuideError(null);
      try {
        // Damos un momento a que la pantalla nueva (tras un navigate) cargue/renderice
        // antes de capturar su mapa, así el agente ve los elementos reales.
        await new Promise((r) => setTimeout(r, 600));
        const { data } = await apiClient.post(
          '/support/guide',
          { sessionId, goal, screen: window.location.pathname, screenElements: captureScreenMap() },
          { timeout: 130000 }
        );
        turns.current += 1;
        const more = (data?.steps ?? []) as GuidedStep[];
        if (!data?.done && more.length) {
          setAllSteps((s) => [...s, ...more]);
          setIndex((i) => i + 1);
          setLoadingNext(false);
          return;
        }
        // done=true sin más pasos → guía completada.
      } catch (e: any) {
        // NO cerramos en silencio: mostramos el error para no perder la guía.
        setLoadingNext(false);
        advancing.current = false; // liberar para que "Reintentar" funcione
        setGuideError(
          e?.response?.status
            ? `No pude traer el siguiente paso (error ${e.response.status}).`
            : 'No pude traer el siguiente paso (sin conexión con el asistente).'
        );
        return;
      }
      setLoadingNext(false);
    }
    finish();
  }, [isLast, canContinue, sessionId, goal, finish]);

  useEffect(() => {
    if (!step) return;
    let cancelled = false;
    advancing.current = false; // nuevo paso renderizado: listo para el próximo avance
    setRect(null);
    setNotFound(false);

    // navigate: cambiamos de ruta y avanzamos automáticamente.
    const route = step.action === 'navigate' ? resolveRoute(step.target) : null;
    if (step.action === 'navigate' && route) {
      record(step, index);
      navigate(route);
      const t = setTimeout(() => !cancelled && advance(), 700);
      return () => {
        cancelled = true;
        clearTimeout(t);
      };
    }

    // highlight/click/fill/info: buscamos el elemento (puede tardar en montar).
    let tries = 0;
    let clickCleanup: (() => void) | null = null;
    const tick = () => {
      if (cancelled) return;
      const el = findElement(step.target, step.value);
      if (el) {
        record(step, index, el);
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => !cancelled && setRect(el.getBoundingClientRect()), 250);
        // AUTO-AVANCE en pasos 'click': cuando el cliente toca ESE elemento (ej.
        // "Nuevo Cliente"), avanzamos solos tras un instante para que la pantalla
        // nueva (el modal recién abierto) renderice y el mapa la capture. Así no
        // hay que tocar "Siguiente" después de abrir el modal (era la fricción).
        if (step.action === 'click') {
          const onTargetClick = () => {
            if (cancelled) return;
            setTimeout(() => !cancelled && advance(), 550);
          };
          el.addEventListener('click', onTargetClick, { once: true });
          clickCleanup = () => el.removeEventListener('click', onTargetClick);
        } else if (
          el instanceof HTMLInputElement ||
          el instanceof HTMLSelectElement ||
          el instanceof HTMLTextAreaElement
        ) {
          // highlight/fill sobre un CAMPO: cuando el cliente lo completa (change),
          // avanzamos al próximo campo. Si es el último paso del tramo, avanzar
          // dispara el fetch del próximo tramo (así, cuando el agente guía un campo
          // a la vez, no queda colgado esperando "Siguiente"). El guard `advancing`
          // evita fetches múltiples por selects en cascada.
          const onFieldChange = () => {
            if (cancelled) return;
            const v = (el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
            if (!v || !v.trim()) return; // no avanzar si quedó vacío
            setTimeout(() => !cancelled && advance(), 400);
          };
          el.addEventListener('change', onFieldChange, { once: true });
          clickCleanup = () => el.removeEventListener('change', onFieldChange);
        }
      } else if (tries++ < 40) {
        // Hasta ~6s: el target puede tardar si la pantalla recién navegó y carga datos.
        setTimeout(tick, 150);
      } else {
        setNotFound(true); // sin elemento, igual mostramos la instrucción
      }
    };
    tick();
    return () => {
      cancelled = true;
      clickCleanup?.();
    };
  }, [step, navigate, advance]);

  // El cliente avanza con "Siguiente"; además, en pasos 'click' avanzamos solos
  // cuando toca el elemento resaltado (ver effect de arriba). Los `navigate` son
  // transiciones y avanzan solos en su effect.

  // Reposicionar el resaltado al hacer scroll/resize.
  useEffect(() => {
    if (!step || !step.target) return;
    const reposition = () => {
      const el = findElement(step.target, step.value);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [step]);

  if (!step) return null;
  if (step.action === 'navigate') return null; // transición, sin UI

  // Nunca mostramos un `ref` crudo (ej. "g48") al cliente: no significa nada.
  const targetIsRef = isRef(step.target);
  const label = step.label || (targetIsRef ? 'Sigue la indicación en pantalla' : step.target) || 'Continúa aquí';
  // Sin elemento resaltado (el agente no lo ubicó): el cliente depende del TEXTO,
  // así que mostramos un tooltip MÁS GRANDE con la instrucción destacada.
  const big = notFound || !rect;
  // Ancho del tooltip acotado al viewport: en pantallas angostas nunca se sale.
  const tipW = Math.min(big ? 420 : 320, window.innerWidth - 24);
  // Tooltip: si el elemento está en la mitad inferior, lo ponemos ARRIBA (para no
  // taparlo ni los botones de abajo); si no, debajo. Si NO hay elemento que anclar,
  // lo fijamos ABAJO (no en el medio, que tapa el contenido y molesta).
  const placeAbove = !!rect && rect.top > window.innerHeight / 2;
  const tipTop = rect
    ? placeAbove
      ? Math.max(12, rect.top - 160)
      : Math.min(rect.bottom + 12, window.innerHeight - 180)
    : window.innerHeight - 220; // abajo
  const tipLeft = rect
    ? Math.min(Math.max(rect.left, 12), window.innerWidth - (tipW + 12))
    : window.innerWidth / 2 - tipW / 2; // centrado horizontal, pegado abajo

  return (
    // pointer-events-none en el contenedor: el portal de abajo queda CLICKEABLE
    // (el cliente puede tocar el elemento resaltado). Solo el tooltip recibe clics.
    <div className="fixed inset-0 z-[70] pointer-events-none" aria-live="polite" data-support-ui>
      {/* Solo un anillo morado claro alrededor del elemento — SIN oscurecer la
          pantalla, para no tapar ni bloquear el formulario del portal. No recibe clics. */}
      {rect && (
        <div
          className="pointer-events-none absolute rounded-lg ring-4 ring-primary-400 ring-offset-2 transition-all"
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
            boxShadow: '0 0 0 3px rgba(148,119,171,0.45)',
          }}
        />
      )}

      {/* Tooltip con la instrucción (único elemento que recibe clics). stopPropagation
          en mousedown: evita que un modal del portal (Headless UI Dialog) lo tome como
          clic-afuera y se cierre al tocar Siguiente. */}
      <div
        className={`pointer-events-auto absolute max-w-[92vw] rounded-xl bg-white shadow-2xl ${big ? 'p-5 ring-1 ring-primary-100' : 'p-4'}`}
        style={{ top: tipTop, left: tipLeft, width: tipW }}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary-500">
          Paso {index + 1} de {allSteps.length}
        </div>
        <Markdown text={label} className={`text-gray-900 ${big ? 'text-lg font-semibold leading-snug' : 'text-sm'}`} />
        {notFound && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-primary-50 p-3">
            <svg className="mt-0.5 h-5 w-5 shrink-0 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <p className="text-sm text-gray-700">
              {step.target && !targetIsRef
                ? <>Busca <span className="font-semibold text-gray-900">«{step.target}»</span> en esta pantalla y luego toca <span className="font-semibold">Siguiente</span>.</>
                : 'Sigue esta indicación en la pantalla y luego toca Siguiente.'}
            </p>
          </div>
        )}
        {loadingNext && (
          <p className="mt-2 flex items-center gap-2 text-xs text-primary-500">
            <span className="h-3 w-3 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
            Preparando el siguiente paso…
          </p>
        )}
        {guideError && <p className="mt-2 text-xs text-red-600">{guideError}</p>}
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800">
            Cerrar
          </button>
          <button
            onClick={() => { setGuideError(null); advance(); }}
            disabled={loadingNext}
            className="rounded-lg bg-primary-700 px-4 py-1.5 text-sm font-semibold text-white hover:bg-primary-800 disabled:opacity-60"
          >
            {guideError ? 'Reintentar' : isLast ? (canContinue ? 'Siguiente' : 'Listo') : 'Siguiente'}
          </button>
        </div>
      </div>
    </div>
  );
}
